import { AxiosResponse } from "axios";
import moment from "moment";
import React, { useMemo, useRef, useState } from "react";
import { ArrowRight } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Button from "reactstrap/es/Button";
import {
  getMonthlyStudentPerformance,
  getMonthlyStudentProgress,
  getStudentAvgAppUsage,
  getStudentPerformance,
} from "../Services/Reports/ReportService";
import { ICsvDownloadHeaders, ISelectedFilters } from "../types";
import { STATE_OFFICIAL_PATHS } from "../routes";
import ConfirmModal from "./Modals/ConfirmModal";
import { CSVLink } from "react-csv";
import { formatDataForCsv } from "../utilityFunctions";

type Props = {
  selectedFilters: ISelectedFilters;
  title: string;
  reportType:
    | "appUsage"
    | "contentProgress"
    | "performance"
    | "performanceImprovement";
};

const HeaderWithDownload = ({ selectedFilters, title, reportType }: Props) => {
  const session = useSelector((state: any) => state.user.session);
  const history = useHistory();
  const refDownloadFile = useRef<any>();
  const [isDownloadingReport, setIsDownloadingReport] =
    useState<boolean>(false); // for download file loader
  const [showFilterAlert, setShowFilterAlert] = useState<boolean>(false);

  const fileName = useMemo(() => {
    let prefix =
      reportType === "appUsage"
        ? `App_usage_report`
        : reportType === "contentProgress"
        ? `content_usage_report`
        : reportType === "performance"
        ? `performance_usage_report`
        : reportType === "performanceImprovement"
        ? `performance_improvement_report`
        : "report";
    let date = selectedFilters?.date
      ? `${moment(selectedFilters?.date?.start_date).format(
          "DD-MM-YYYY"
        )}_to_${moment(selectedFilters?.date?.end_date).format("DD-MM-YYYY")}`
      : moment().format("DD-MM-YYYY");
    return `${prefix}_${date}`;
  }, [reportType, selectedFilters?.date]);

  const [reportData, setReportData] = useState<{
    data: any[];
    headers: ICsvDownloadHeaders[];
  }>({
    data: [],
    headers: [],
  });

  const apiEndPoint = useMemo(
    () =>
      reportType === "appUsage"
        ? getStudentAvgAppUsage
        : reportType === "performanceImprovement"
        ? getStudentPerformance
        : reportType === "contentProgress"
        ? getMonthlyStudentProgress
        : reportType === "performance"
        ? getMonthlyStudentPerformance
        : undefined,
    [reportType]
  );
  const handleDownloadReport = (filters: ISelectedFilters) => {
    setIsDownloadingReport(true);

    apiEndPoint(
      10000, //data limit
      0, //offset
      session?.id,
      filters?.districtId,
      filters?.blockId,
      filters?.schoolId,
      filters?.gradeId,
      filters?.date?.start_date,
      filters?.date?.end_date
    )
      .then(
        (response: AxiosResponse) => {
          let { data } = response?.data;
          const result = formatDataForCsv(reportType, data);
          setReportData({
            data: result?.data,
            headers: result?.headers,
          });
          refDownloadFile?.current?.getElementsByTagName("a")[0]?.click();
        },
        (error) => {
          console.log("Error: ", error?.response);
          toast.error(
            "Sorry! Could not generate report. Please try again later."
          );
          setIsDownloadingReport(false);
        }
      )
      .finally(() => {
        setIsDownloadingReport(false);
      });
  };
  return (
    <>
      <ConfirmModal
        isOpen={showFilterAlert}
        onClose={() => setShowFilterAlert((prev) => !prev)}
        type={"warning"}
        title={`Data size will be too large.`}
        subtitle="Please at least select filters for district & block"
        hideCancelButton
        onConfirm={() => {
          setShowFilterAlert(false);
        }}
      />
      <div className="d-flex justify-content-between">
        <h3 className="mb-0 text-muted">
          <i className="fas fa-file-invoice mr-1"></i> {title}
        </h3>
        <div>
          <div>
            <Button
              color="primary"
              size="sm"
              className="mr-2"
              disabled={isDownloadingReport}
              onClick={() => {
                if (selectedFilters?.districtId && selectedFilters?.blockId) {
                  handleDownloadReport(selectedFilters);
                } else {
                  setShowFilterAlert(true);
                }
              }}
            >
              {isDownloadingReport ? (
                "Downloading.Please wait"
              ) : (
                <>
                  Download File <i className="fas fa-file-download"></i>
                </>
              )}
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={() => history.push(STATE_OFFICIAL_PATHS.reports)}
            >
              Go Back <ArrowRight />
            </Button>
          </div>
        </div>
      </div>
      <button ref={refDownloadFile} className="d-none">
        <CSVLink
          data={reportData?.data}
          headers={reportData?.headers}
          filename={`${fileName}.csv`}
        ></CSVLink>
      </button>
    </>
  );
};

export default HeaderWithDownload;
